import React, { useEffect, useState } from 'react';
import {
  ArrowLeft, ChevronLeft, ClipboardList, History, Loader2, Timer, Trash2,
} from 'lucide-react';
import { useUI, useData, useActions } from '../../context/AppContext';
import StudentDetailSidebar from '../../components/instructor/StudentDetailSidebar';
import StudentDetailTasksTab from '../../components/instructor/StudentDetailTasksTab';
import StudentDetailHistoryTab from '../../components/instructor/StudentDetailHistoryTab';
import StudentDetailPerformanceTab from '../../components/instructor/StudentDetailPerformanceTab';
import { parseTRDate } from '../../utils/dateUtils';
import { useMdUp } from '../../hooks/useMdUp';

const DETAIL_SECTIONS = [
  { id: 'tasks', label: 'Görevler', icon: ClipboardList, hint: 'Aktif antrenman programları' },
  { id: 'history', label: 'Geçmiş', icon: History, hint: 'Arşivlenen programlar' },
  { id: 'performance', label: 'Dereceler', icon: Timer, hint: 'Yüzme kayıtları' },
];

const CARD_CLASS =
  'instructor-surface rounded-3xl';

const StudentDetail = () => {
  const { selectedStudent, setActiveModal, goToInstructorTab } = useUI();
  const { students, selectedStudentSubData } = useData();
  const {
    handleDeleteProgram,
    handleDeleteProgramPermanent,
    handleDeleteSwimmingRecord,
  } = useActions();
  const isMdUp = useMdUp();
  const [subTab, setSubTab] = useState('tasks');
  const [mobileHub, setMobileHub] = useState(true);

  useEffect(() => {
    setMobileHub(true);
    setSubTab('tasks');
  }, [selectedStudent?.id]);

  if (!selectedStudent) return null;
  const syncStudent = students.find((s) => s.id === selectedStudent.id) || selectedStudent;

  const { swimmingRecords, programs, isLoading } = selectedStudentSubData;

  const activeTasks = programs
    .filter((p) => !p.archived)
    .sort((a, b) => {
      const da = a.dateISO ? a.dateISO.replaceAll('-', '') : String(parseTRDate(a.date));
      const db_ = b.dateISO ? b.dateISO.replaceAll('-', '') : String(parseTRDate(b.date));
      return da.localeCompare(db_);
    });
  const archivedTasks = programs
    .filter((p) => p.archived)
    .sort((a, b) => (b.archivedAt || b.timestamp || 0) - (a.archivedAt || a.timestamp || 0));

  const activeSection = DETAIL_SECTIONS.find((s) => s.id === subTab) || DETAIL_SECTIONS[0];
  const showMobileHub = !isMdUp && mobileHub;
  const showMobileContent = !isMdUp && !mobileHub;

  const openMobileSection = (id) => {
    setSubTab(id);
    setMobileHub(false);
  };

  const tabContent = isLoading ? (
    <div className="flex items-center justify-center py-16 text-slate-400 gap-3">
      <Loader2 size={22} className="animate-spin" />
      <span className="font-bold">Öğrenci verileri yükleniyor...</span>
    </div>
  ) : subTab === 'tasks' ? (
    <StudentDetailTasksTab
      activeTasks={activeTasks}
      studentId={syncStudent.id}
      setActiveModal={setActiveModal}
      handleDeleteProgram={handleDeleteProgram}
      handleDeleteProgramPermanent={handleDeleteProgramPermanent}
    />
  ) : subTab === 'history' ? (
    <StudentDetailHistoryTab
      archivedTasks={archivedTasks}
      studentId={syncStudent.id}
      handleDeleteProgramPermanent={handleDeleteProgramPermanent}
    />
  ) : (
    <StudentDetailPerformanceTab
      swimmingRecords={swimmingRecords}
      studentId={syncStudent.id}
      setActiveModal={setActiveModal}
      handleDeleteSwimmingRecord={handleDeleteSwimmingRecord}
    />
  );

  const sectionLabels = {
    tasks: 'Görevler',
    history: `Geçmiş (${archivedTasks.length})`,
    performance: 'Dereceler',
  };

  return (
    <div className="p-4 sm:p-8 animate-in slide-in-from-right">
      {/* ── Mobil hub ─────────────────────────────────────────── */}
      {showMobileHub && (
        <div className="space-y-4">
          <button
            type="button"
            onClick={() => goToInstructorTab('students')}
            className="flex items-center gap-2 text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 font-bold transition-colors"
          >
            <ArrowLeft size={20} /> Listeye Geri Dön
          </button>

          <StudentDetailSidebar syncStudent={syncStudent} />

          <div className={`${CARD_CLASS} p-2`}>
            <nav aria-label="Öğrenci bölümleri" className="flex flex-col gap-1">
              {DETAIL_SECTIONS.map((item) => {
                const Icon = item.icon;
                const label = item.id === 'history'
                  ? `Geçmiş (${archivedTasks.length})`
                  : item.label;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => openMobileSection(item.id)}
                    className="flex items-center gap-3 px-3 py-3 rounded-2xl text-left text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
                  >
                    <Icon size={18} className="shrink-0 text-slate-400" />
                    <span className="min-w-0">
                      <span className="block text-sm font-bold">{label}</span>
                      <span className="block text-[11px] font-medium text-slate-400 dark:text-slate-500 truncate">
                        {item.hint}
                      </span>
                    </span>
                  </button>
                );
              })}
            </nav>
          </div>

          <button
            type="button"
            onClick={() => setActiveModal('deleteConfirm')}
            className="w-full flex items-center justify-center gap-2 text-red-500 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 px-4 py-3.5 rounded-2xl font-bold transition-colors"
          >
            <Trash2 size={18} /> Öğrenciyi Sil
          </button>
        </div>
      )}

      {/* ── Mobil bölüm içeriği ──────────────────────────────── */}
      {showMobileContent && (
        <div className="space-y-4">
          <button
            type="button"
            onClick={() => setMobileHub(true)}
            className="inline-flex items-center gap-1.5 text-sm font-bold text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
          >
            <ChevronLeft size={18} />
            {syncStudent.name}
          </button>
          <h2 className="text-lg font-black text-slate-800 dark:text-white">
            {activeSection.id === 'history'
              ? `Geçmiş (${archivedTasks.length})`
              : activeSection.label}
          </h2>
          <div className={`${CARD_CLASS} p-4 min-h-[320px]`}>
            {tabContent}
          </div>
        </div>
      )}

      {/* ── Masaüstü (değişmeden) ─────────────────────────────── */}
      {isMdUp && (
        <>
          <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 mb-6">
            <button
              type="button"
              onClick={() => goToInstructorTab('students')}
              className="flex items-center justify-center sm:justify-start gap-2 text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 font-bold transition-colors"
            >
              <ArrowLeft size={20} /> Listeye Geri Dön
            </button>
            <button
              type="button"
              onClick={() => setActiveModal('deleteConfirm')}
              className="flex items-center justify-center gap-2 text-red-500 bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/40 px-4 py-2 rounded-lg font-bold transition-colors"
            >
              <Trash2 size={18} /> Öğrenciyi Sil
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
            <StudentDetailSidebar syncStudent={syncStudent} />

            <div className="lg:col-span-2 space-y-6">
              <div className="flex flex-col sm:flex-row bg-slate-100 dark:bg-slate-800 p-1.5 rounded-2xl w-full gap-1">
                {DETAIL_SECTIONS.map(({ id }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setSubTab(id)}
                    className={`flex-1 py-3 rounded-xl font-bold text-sm transition-colors ${
                      subTab === id
                        ? 'bg-white dark:bg-slate-700 shadow-sm text-blue-600 dark:text-blue-400'
                        : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'
                    }`}
                  >
                    {sectionLabels[id]}
                  </button>
                ))}
              </div>

              <div className="bg-white dark:bg-slate-800 p-4 sm:p-8 rounded-3xl border border-slate-100 dark:border-slate-700 shadow-sm min-h-[400px]">
                {tabContent}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default StudentDetail;
